import { useCallback, useContext, useEffect, useState } from 'react';

import { ComputedDatum } from '@nivo/pie';
import { useHistory } from 'react-router-dom';

import { getOnSiteTradesDataRequest, getCompaniesWidgetsSharedData } from '@vyce/core/src/api/time';
import { generateQueryParamsFromObj } from '@vyce/core/src/utils/url';

import { Props, WidgetDataItem } from '../types';
import { NotificationContext } from '../../../../contexts/notificationContext';
import { useWidgetErrorHandler } from '@vyce/core/src/hooks/useWidgetErrorHandler';
import { generateExcelFileLink } from '../../../../utils';
import { useBooleanState } from '../../../../hooks';
import { useMainDashboardFilter } from '../../../../hooks/useMainDashboardFilter';
import { AxiosError } from 'axios';

export const useData = ({ selectedCompanyId, mainDashboardFilters, onForbidden }: Props) => {
  const [loading, setLoading] = useState(false);
  const [downloadButtonLoading, setDownloadButtonLoadingTrue, setDownloadButtonLoadingFalse] =
    useBooleanState(false);
  const { handleServerError } = useContext(NotificationContext);
  // Loading the widget: a 403 hides it instead of raising a toast.
  const handleWidgetError = useWidgetErrorHandler(onForbidden);
  const [widgetData, setWidgetData] = useState<WidgetDataItem[]>([]);
  const history = useHistory();

  const { selectedLocationOptions, getLocationsForRequest } = useMainDashboardFilter({
    mainDashboardFilters,
  });

  const fetchData = useCallback(
    async (signal: AbortSignal) => {
      try {
        setLoading(true);
        const { data } = await getOnSiteTradesDataRequest(
          selectedCompanyId,
          {
            site_ids: getLocationsForRequest(selectedLocationOptions.length ? 'selected' : 'all'),
          },
          signal
        );

        const preparedData = data.trades.map(item => ({
          id: item.trade,
          label: item.trade,
          value: item.count,
          query_field: { role: item.trade },
        }));
        setWidgetData(preparedData);
        setLoading(false);
      } catch (e) {
        const err = e as AxiosError;
        if (err.name !== 'CanceledError') {
          handleWidgetError(err);
          setLoading(false);
        }
      }
    },
    [selectedCompanyId, getLocationsForRequest]
  );

  const getSharedFile = async () => {
    if (!selectedCompanyId) {
      return;
    }
    try {
      setDownloadButtonLoadingTrue();
      const res = await getCompaniesWidgetsSharedData({
        companyId: selectedCompanyId,
        target: 'on_site_trades',
        payload: {},
      });
      generateExcelFileLink(res);
    } catch (e) {
      handleServerError(e);
    } finally {
      setDownloadButtonLoadingFalse();
    }
  };

  const handleChartClick = (datum: ComputedDatum<any>) => {
    const query = generateQueryParamsFromObj(datum.data.query_field);
    history.push(`/time/users?${query}&user_status=Checked in`);
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    fetchData(signal);

    return () => {
      controller.abort();
    };
  }, [fetchData]);

  return { widgetData, loading, downloadButtonLoading, getSharedFile, handleChartClick };
};
